import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";
import "../../styles/booking-step-header.scss";

export interface StepInfo {
  name: string;
  stage: string;
}

interface BookingStepHeaderProps {
  readonly venueName?: string;
  readonly currentStage?: string;
  readonly steps?: StepInfo[];
  readonly onBack?: () => void;
  /** Keeps Back visible but unusable, e.g. while a booking or payment request runs. */
  readonly backDisabled?: boolean;
  /**
   * Render the venue name on the header's left. A flow that already states it
   * elsewhere on the same screen passes `false` to avoid repeating it — on
   * `/book` that is true only while the venue sidebar is up, so it is passed
   * per stage rather than once for the flow.
   */
  readonly showBranding?: boolean;
}

const BookingStepHeader: React.FC<BookingStepHeaderProps> = ({
  venueName,
  currentStage = "",
  steps = [],
  onBack,
  backDisabled = false,
  showBranding = true,
}) => {
  const { t } = useTranslation();

  const currentIndex = steps.findIndex((s) => s.stage === currentStage);
  const currentStep = currentIndex >= 0 ? steps[currentIndex] : undefined;
  const stepNumber = currentIndex + 1;
  const totalSteps = steps.length;

  return (
    <div className={`booking-step-header ${currentStep?.stage !== "details" ? "gcw:p-6" : ""}`}>
      {/* Kept in the DOM even when empty: the header is `space-between`, so
          without the left group the step indicator would slide to the left. */}
      <div className="booking-step-header__left gcw:gap-6">
        {showBranding && venueName && (
          /* `venue-name` is the marker a surface that flattens the type scale
             exempts, so the name keeps its step — the same one
             BookingInfoSidebar and BookingInfoInline carry. */
          <span className="venue-name gcw:hidden gcw:lg:block gcw:text-text-primary gcw:font-bold gcw:text-2xl">
            {venueName}
          </span>
        )}
      </div>
      <div className="booking-step-header__right gcw:flex gcw:items-center gcw:gap-2 gcw:shrink-0 gcw:px-4">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            disabled={backDisabled}
            className="gcw:disabled:opacity-50 gcw:disabled:cursor-not-allowed gcw:text-text-brand-blue gcw:font-semibold gcw:text-body gcw:block gcw:underline gcw:cursor-pointer gcw:bg-transparent gcw:border-none gcw:p-0"
          >
            <span className="gcw:flex gcw:items-center">
              <ChevronLeft className="gcw:text-text-brand-blue" size={20} />
              {t("back", "Back")}
            </span>
          </button>
        )}
        {currentStep && (
          <div className="booking-step-header__steps gcw:flex gcw:items-center gcw:gap-2">
            <span className="gcw:text-text-steps gcw:text-body">
              {t(`booking_step_${currentStep.stage}`, currentStep.name)}
            </span>
            <span className="gcw:text-text-steps gcw:text-body" aria-hidden="true">
              {"\u00B7"}
            </span>
            <span className="gcw:text-text-steps gcw:text-body gcw:inline-flex gcw:items-center gcw:whitespace-nowrap">
              {t("booking_step_of", "Step {{current}} of {{total}}", { current: stepNumber, total: totalSteps })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingStepHeader;
